// Compare panes (PN1) — a DEVELOPER screen, unlinked like /usage: one question asked
// of three model configurations side by side, for judging the answers by eye. Not a product surface.
//
// Each pane is the chat's own machinery, not a copy of it: its own <SqAiProvider> (a socket and the
// SDK's state machine) around the chat's own <Thread>, so the grey progress lines, the chart, the table
// with its real header and "Showing N of M" line, the follow-ups and the grey chips render exactly as
// they do at /sq-ai. A reimplemented pane would not show the defects this screen exists to find.
//
// A FRESH CONVERSATION PER PANE PER ASK. Every Ask remounts each pane's provider under a new
// conversationId (and a new socket), because history changes routing — three panes sharing one would
// compare the history, not the models. The scope the chat page sends is mirrored (uiMode "prod",
// devSql off, web search asked for, the untouched picker's context), so a pane's prompt is the chat's
// prompt. The pane's model and config ride the opening turn as `modelOverride`; the server refuses it
// unless SQ_AI_COMPARE_PANES is on, and the refusal shows in the pane.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { SqAiProvider, useSqAi } from "@safetyqube/sq-ai-react";
import { tenantApi } from "../../services/api";
import Thread from "./components/Thread";
import styles from "./sqAi.module.css";

const PANES = [0, 1, 2];
const ENVS = ["prod", "dev"];
const MODES = ["chart", "general", "summarise"];
const PREFERRED_TENANT = "myton";
const USER_TZ = "Asia/Calcutta";

const S = {
  page: { height: "100vh", display: "flex", flexDirection: "column", background: "#f4f5f7", fontSize: 13 },
  bar: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, padding: "8px 12px", background: "#fff", borderBottom: "1px solid #e2e4e8" },
  form: { display: "flex", gap: 6, flex: "1 1 420px" },
  input: { flex: 1, padding: "6px 8px", border: "1px solid #c9ccd3", borderRadius: 4, fontSize: 14 },
  tag: { fontSize: 11, color: "#8a5a00", background: "#fff4d6", borderRadius: 3, padding: "1px 6px" },
  notice: { padding: "6px 12px", background: "#fff4d6", color: "#6b4600", borderBottom: "1px solid #f0dca0" },
  asked: { padding: "6px 12px", background: "#eef3ff", borderBottom: "1px solid #d6e0f5" },
  grid: { flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, padding: 8 },
  pane: { display: "flex", flexDirection: "column", minHeight: 0, background: "#fff", border: "1px solid #e2e4e8", borderRadius: 6 },
  paneHead: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", padding: "6px 10px", borderBottom: "1px solid #eceef1" },
  meta: { padding: "4px 10px", color: "#555", borderBottom: "1px solid #eceef1", minHeight: 20 },
  scroll: { flex: 1, minHeight: 0, overflow: "auto", padding: "8px 12px" },
  select: { maxWidth: 280 },
};

function newId() {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return "cmp-" + Date.now() + "-" + Math.floor(Math.random() * 1000000);
}

// What the chat page's picker sends with nothing picked — usePicker's `context` over its initial state.
function untouchedPickerContext(tenantSchema) {
  return { tenant: tenantSchema || null, modules: [], templatesByModule: {}, templates: [], facility: null };
}

export default function ComparePage() {
  const [env, setEnv] = useState("prod");
  const [tenants, setTenants] = useState([]);
  const [tenantSchema, setTenantSchema] = useState("");
  const [tenantsError, setTenantsError] = useState(null);
  const [mode, setMode] = useState("chart");
  const [draft, setDraft] = useState("");
  // { id, text, settings } — the settings are frozen at Ask, so a dropdown changed afterwards labels
  // the NEXT ask and never the answer already on screen.
  const [ask, setAsk] = useState(null);
  // undefined until a pane's socket has connected; null when the server has the switch off.
  const [options, setOptions] = useState(undefined);
  const [settings, setSettings] = useState([null, null, null]);
  const [running, setRunning] = useState([false, false, false]);

  useEffect(() => {
    let cancelled = false;
    setTenantsError(null);
    tenantApi
      .getAllForEnv(env)
      .then((res) => {
        if (cancelled) return;
        const list = (Array.isArray(res.data) ? res.data : res.data.tenants || []).map((t) => t.name || t.schema || t);
        setTenants(list);
        setTenantSchema((cur) => (list.includes(cur) ? cur : list.includes(PREFERRED_TENANT) ? PREFERRED_TENANT : list[0] || ""));
      })
      .catch((err) => {
        if (!cancelled) setTenantsError(err.message || "Could not load tenants");
      });
    return () => {
      cancelled = true;
    };
  }, [env]);

  const onOptions = useCallback((opts) => {
    setOptions(opts);
    if (opts && Array.isArray(opts.defaults)) {
      setSettings((cur) => (cur.every(Boolean) ? cur : cur.map((s, i) => s || opts.defaults[i] || null)));
    }
  }, []);
  const onRunning = useCallback((i, busy) => {
    setRunning((cur) => (cur[i] === busy ? cur : cur.map((c, k) => (k === i ? busy : c))));
  }, []);
  const onSetting = useCallback((i, patch) => {
    setSettings((cur) => cur.map((c, k) => (k === i ? { ...c, ...patch } : c)));
  }, []);

  const anyRunning = running.some(Boolean);
  const canAsk = Boolean(draft.trim()) && Boolean(tenantSchema) && Boolean(options) && settings.every(Boolean) && !anyRunning;

  const fire = (e) => {
    e.preventDefault();
    if (!canAsk) return;
    setAsk({ id: newId(), text: draft.trim(), settings: settings.slice() });
  };
  // A scope change starts every pane over — an answer from another tenant or mode is not comparable.
  const changeScope = (setter) => (e) => {
    setter(e.target.value);
    setAsk(null);
  };

  return (
    <div style={S.page}>
      <div style={S.bar}>
        <strong>Compare panes</strong>
        <span style={S.tag}>developer tool</span>
        <form onSubmit={fire} style={S.form}>
          <input style={S.input} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Ask all three…" aria-label="Question" />
          <button type="submit" disabled={!canAsk}>Ask</button>
        </form>
        <label>
          env{" "}
          <select value={env} onChange={changeScope(setEnv)} disabled={anyRunning}>
            {ENVS.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
        <label>
          tenant{" "}
          <select value={tenantSchema} onChange={changeScope(setTenantSchema)} disabled={anyRunning || !tenants.length}>
            {tenants.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label>
          mode{" "}
          <select value={mode} onChange={changeScope(setMode)} disabled={anyRunning}>
            {MODES.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
      </div>
      {options === null && (
        <div style={S.notice}>This server has model overrides switched off — start it with SQ_AI_COMPARE_PANES=1. Until then every pane's ask is refused.</div>
      )}
      {tenantsError && <div style={S.notice}>Could not load tenants: {tenantsError}</div>}
      {ask && (
        <div style={S.asked}>
          <strong>Asked:</strong> {ask.text}{" "}
          <span style={{ color: "#667" }}>· {env} · {tenantSchema} · {mode} mode · a fresh conversation in each pane</span>
        </div>
      )}
      <div style={S.grid}>
        {PANES.map((i) => (
          <Pane
            key={i}
            index={i}
            ask={ask}
            env={env}
            tenantSchema={tenantSchema}
            mode={mode}
            options={options}
            setting={settings[i]}
            onSetting={onSetting}
            onOptions={onOptions}
            onRunning={onRunning}
            disabled={anyRunning}
          />
        ))}
      </div>
    </div>
  );
}

function Pane({ index, ask, env, tenantSchema, mode, options, setting, onSetting, onOptions, onRunning, disabled }) {
  const asked = ask ? ask.settings[index] : null;
  // The key IS the conversation: a new ask, or a new scope, remounts the provider — a new socket, a
  // new conversationId, an empty state machine.
  const conversationId = ask ? `${ask.id}-pane${index + 1}` : `idle-${env}-${tenantSchema}-${mode}-pane${index + 1}`;
  const models = (options && options.models) || [];
  const configs = (options && options.configs) || [];
  return (
    <section style={S.pane}>
      <div style={S.paneHead}>
        <strong>Pane {index + 1}</strong>
        <label>
          model{" "}
          <select style={S.select} value={setting ? setting.model : ""} onChange={(e) => onSetting(index, { model: e.target.value })} disabled={disabled || !setting}>
            {models.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </label>
        <label>
          config{" "}
          <select style={S.select} value={setting ? setting.config : ""} onChange={(e) => onSetting(index, { config: e.target.value })} disabled={disabled || !setting}>
            {configs.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </label>
      </div>
      <SqAiProvider
        key={conversationId}
        conversationId={conversationId}
        tenantSchema={tenantSchema}
        context={untouchedPickerContext(tenantSchema)}
        mode={mode}
        auth={{ env, confidentialAccess: false, userTz: USER_TZ, webSearch: true }}
        uiMode="prod"
        devSql={false}
        turnExtras={asked ? { modelOverride: { model: asked.model, config: asked.config } } : undefined}
      >
        <PaneBody
          index={index}
          ask={ask}
          asked={asked}
          options={options}
          env={env}
          tenantSchema={tenantSchema}
          conversationId={conversationId}
          onOptions={onOptions}
          onRunning={onRunning}
        />
      </SqAiProvider>
    </section>
  );
}

function PaneBody({ index, ask, asked, options, env, tenantSchema, conversationId, onOptions, onRunning }) {
  const { state, send, connected, connectionError, socket, sessionPosture } = useSqAi();
  const scrollRef = useRef(null);
  const sentRef = useRef(false);
  const [startedAt, setStartedAt] = useState(null);
  const [endedAt, setEndedAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [ranOn, setRanOn] = useState(null);

  useEffect(() => {
    if (sessionPosture) onOptions(sessionPosture.compare || null);
  }, [sessionPosture, onOptions]);

  useEffect(() => {
    if (!ask || sentRef.current || !connected || !tenantSchema) return;
    sentRef.current = true;
    setStartedAt(Date.now());
    send(ask.text);
  }, [ask, connected, tenantSchema, send]);

  // Which model the SERVER ran each call on, read off ai:done's per-call rows — the pane's own proof
  // that the override was honoured, rather than the dropdown's say-so.
  useEffect(() => {
    if (!socket) return undefined;
    const onDone = (p) => {
      if (!p || p.conversationId !== conversationId) return;
      const calls = p.tokens && Array.isArray(p.tokens.calls) ? p.tokens.calls : [];
      if (!calls.length) return;
      const ranModels = Array.from(new Set(calls.map((c) => c.model))).join(", ");
      setRanOn(`${ranModels} · ${calls.length} ${calls.length === 1 ? "call" : "calls"}`);
    };
    socket.on("ai:done", onDone);
    return () => socket.off("ai:done", onDone);
  }, [socket, conversationId]);

  const last = state.messages[state.messages.length - 1];
  const finished = Boolean(last && last.role === "assistant" && last.done);
  useEffect(() => {
    if (finished && startedAt && !endedAt) setEndedAt(Date.now());
  }, [finished, startedAt, endedAt]);
  const busy = Boolean(ask) && !finished && !connectionError;
  useEffect(() => {
    onRunning(index, busy);
  }, [index, busy, onRunning]);
  useEffect(() => {
    if (!startedAt || endedAt) return undefined;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [startedAt, endedAt]);

  const seconds = startedAt ? (((endedAt || now) - startedAt) / 1000).toFixed(1) : null;
  const preset = asked && options ? options.configs.find((c) => c.value === asked.config) : null;
  return (
    <>
      <div style={S.meta}>
        {asked ? (
          <span>
            <strong>{asked.model}</strong> · {preset ? preset.label : asked.config}
          </span>
        ) : (
          <span>{connected ? "ready" : "connecting…"}</span>
        )}
        {seconds !== null && <span> · {seconds}s{endedAt ? "" : " …"}</span>}
        {ranOn && <span> · ran on {ranOn}</span>}
      </div>
      <div ref={scrollRef} style={S.scroll}>
        {connectionError && (
          <div className={styles.errorBox}>
            <span aria-hidden="true">⚠</span>
            <div>
              <div>{connectionError.message}</div>
              <div className={styles.assumptionRationale}>{connectionError.code}</div>
            </div>
          </div>
        )}
        {state.messages.length > 0 ? (
          <Thread messages={state.messages} onSuggestionClick={() => {}} suggestionsDisabled env={env} tenantSchema={tenantSchema} scrollRef={scrollRef} />
        ) : (
          !connectionError && <p style={{ color: "#888" }}>{ask ? "Connecting…" : "Type a question above — this pane asks it with the model and config chosen here."}</p>
        )}
      </div>
    </>
  );
}
